import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../api';
import { usePermissions } from '../../auth';
import { TableError } from './tableUi';
import { BTN_PRIMARY, CARD, INPUT, LABEL } from '../../components/common/ui';

/** Module toggle: enable/disable + internal vs external (forward to client's system). */
export interface SettingsTabProps {
  /** Changes when the operator switches node (cloud); forces a refetch. */
  nodeKey?: string;
}

export default function SettingsTab({ nodeKey = '' }: SettingsTabProps) {
  const { attendance } = useApi();
  const { hasPermission } = usePermissions();
  const canConfig = hasPermission('attendance.config'); // backend also enforces
  const [cfg, setCfg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try { setCfg(await attendance.getConfig()); setError(''); }
    catch (e: any) { console.error(e); setError(e?.message || 'Request failed'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, nodeKey]);

  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const saved = await attendance.updateConfig({
        enabled: cfg.enabled,
        mode: cfg.mode,
        external_webhook_url: cfg.external_webhook_url,
        work_day_cutoff_hour: Number(cfg.work_day_cutoff_hour),
        confidence_floor: Number(cfg.confidence_floor),
        debounce_seconds: Number(cfg.debounce_seconds),
      });
      setCfg(saved); setMsg('Saved');
    } catch (e: any) { setMsg(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  // A form skeleton rather than a bare "Loading…", so the panel doesn't jump when it lands.
  if (loading) {
    return (
      <div className="max-w-xl space-y-4" aria-busy="true" aria-live="polite" aria-label="Loading settings">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i}>
            <div className="h-3 w-28 animate-pulse rounded bg-muted" />
            <div className="mt-2 h-9 w-full animate-pulse rounded bg-muted" />
          </div>
        ))}
      </div>
    );
  }
  if (!cfg) {
    return (
      <div className={CARD}>
        <TableError message={error} onRetry={reload}>Couldn&apos;t load attendance settings</TableError>
      </div>
    );
  }

  const set = (k: string, v: any) => setCfg((c: any) => ({ ...c, [k]: v }));

  return (
    <div className="max-w-2xl space-y-5">
      <div className={`${CARD} p-4`}>
        <label className="flex items-center justify-between">
          <div>
            <div className="font-medium text-foreground">Attendance module enabled</div>
            <div className="text-xs text-muted-foreground">Master switch. When off, no attendance is computed and face events are untouched.</div>
          </div>
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => set('enabled', e.target.checked)} className="h-5 w-5 accent-[var(--brand)]" />
        </label>
      </div>

      <div className={`${CARD} space-y-3 p-4`}>
        <div className="font-medium text-foreground">Mode</div>
        <label className="flex items-start gap-2">
          <input type="radio" name="mode" checked={cfg.mode === 'internal'} onChange={() => set('mode', 'internal')} className="mt-1 accent-[var(--brand)]" />
          <div><div className="text-sm text-foreground">Internal</div><div className="text-xs text-muted-foreground">We compute attendance (sightings → sessions → daily/monthly).</div></div>
        </label>
        <label className="flex items-start gap-2">
          <input type="radio" name="mode" checked={cfg.mode === 'external'} onChange={() => set('mode', 'external')} className="mt-1 accent-[var(--brand)]" />
          <div><div className="text-sm text-foreground">External</div><div className="text-xs text-muted-foreground">Forward face events to the client's own attendance system; compute nothing here.</div></div>
        </label>
        {cfg.mode === 'external' && (
          <input type="url" placeholder="https://client-system/webhook" value={cfg.external_webhook_url || ''} onChange={(e) => set('external_webhook_url', e.target.value)}
            className={INPUT} />
        )}
      </div>

      <div className={`${CARD} grid grid-cols-1 gap-3 p-4 sm:grid-cols-3`}>
        <Field label="Day cutoff hour" value={cfg.work_day_cutoff_hour} onChange={(v) => set('work_day_cutoff_hour', v)} />
        <Field label="Confidence floor" step="0.05" value={cfg.confidence_floor} onChange={(v) => set('confidence_floor', v)} />
        <Field label="Debounce (sec)" value={cfg.debounce_seconds} onChange={(v) => set('debounce_seconds', v)} />
      </div>

      <div className="flex items-center gap-3">
        {canConfig && <button onClick={save} disabled={saving} className={BTN_PRIMARY}>{saving ? 'Saving…' : 'Save changes'}</button>}
        {msg && <span className="text-sm text-muted-foreground">{msg}</span>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, step }: { label: string; value: any; onChange: (v: any) => void; step?: string }) {
  return (
    <label className="block">
      <span className={LABEL}>{label}</span>
      <input type="number" step={step} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        className={INPUT} />
    </label>
  );
}
